import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CommunityProfile } from '../components/sections/community-profile'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { supabaseBadgeService, type BadgeWithProgress, type UserBadgeWithDetails } from '../lib/supabaseBadgeService'

export function Profile() {
  const { userId } = useParams<{ userId: string }>()
  const { user: currentAuthUser } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [profiles, setProfiles] = useState<any[]>([])
  const [targetUser, setTargetUser] = useState<any | null>(null)
  const [votingHistory, setVotingHistory] = useState<any[]>([])
  const [allBadges, setAllBadges] = useState<BadgeWithProgress[]>([])
  const [userBadges, setUserBadges] = useState<UserBadgeWithDetails[]>([])

  // Determine which user's profile to show
  const effectiveUserId = userId || currentAuthUser?.id

  // Fetch profile data
  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        // If no user is specified and no one is logged in, redirect to sign in
        if (!effectiveUserId) {
          navigate('/signin')
          return
        }

        // Fetch all profiles for leaderboard
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .order('total_score', { ascending: false })
          .limit(50)

        if (profilesError) throw profilesError

        setProfiles(profilesData || [])

        // Find the target user's profile
        const targetProfile = profilesData?.find(p => p.id === effectiveUserId)
        setTargetUser(targetProfile)

        // Fetch voting history and badge data for the target user
        if (effectiveUserId) {
          const { data: votesData, error: votesError } = await supabase
            .from('votes')
            .select(`
              *,
              alternative:alternatives(
                *,
                foreign_word:foreign_words(*)
              )
            `)
            .eq('user_id', effectiveUserId)
            .order('voted_at', { ascending: false })
            .limit(20)

          if (!votesError) {
            setVotingHistory(votesData || [])
          }

          // Fetch badge data
          try {
            const [badgesData, userBadgesData] = await Promise.all([
              supabaseBadgeService.getAllBadgesWithProgress(effectiveUserId),
              supabaseBadgeService.getUserBadges(effectiveUserId)
            ])
            setAllBadges(badgesData)
            setUserBadges(userBadgesData)
          } catch (badgeError) {
            console.error('Failed to load badge data:', badgeError)
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error)
        toast.error('فشل تحميل البيانات')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [effectiveUserId, currentAuthUser, navigate])

  const handleViewProfile = (viewUserId: string) => {
    navigate(`/profile/${viewUserId}`)
  }

  const handleViewVotingHistory = (viewUserId: string) => {
    console.log('View voting history:', viewUserId)
    toast(`عرض تاريخ التصويت للعضو: ${viewUserId}`)
  }

  // Map profiles to users format
  const users = profiles.map(profile => ({
    id: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    joinDate: profile.join_date || profile.created_at,
    wordsSubmitted: profile.words_submitted || 0,
    alternativesProposed: profile.alternatives_proposed || 0,
    votesCast: profile.votes_cast || 0,
    commentsLeft: profile.comments_left || 0,
    totalScore: profile.total_score || 0,
    rank: 0,
  }))

  // Map profiles to userProfiles format
  const userProfilesRecord = profiles.reduce((acc, profile) => {
    acc[profile.id] = {
      id: profile.id,
      name: profile.name,
      avatarUrl: profile.avatar_url,
      joinDate: profile.join_date || profile.created_at,
      wordsSubmitted: profile.words_submitted || 0,
      alternativesProposed: profile.alternatives_proposed || 0,
      votesCast: profile.votes_cast || 0,
      commentsLeft: profile.comments_left || 0,
      totalScore: profile.total_score || 0,
      rank: 0,
      bio: profile.bio || '',
      achievements: [],
      recentActivity: [],
    }
    return acc
  }, {} as Record<string, any>)

  // Create leaderboard entries
  const createLeaderboardEntry = (profile: any, rank: number) => ({
    userId: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    totalScore: profile.total_score || 0,
    rank: rank + 1,
  })

  const createLeaderboardWithCount = (profile: any, rank: number, count: number) => ({
    userId: profile.id,
    name: profile.name,
    avatarUrl: profile.avatar_url,
    count,
    rank: rank + 1,
  })

  // Create leaderboards
  const leaderboards = {
    overall: profiles.slice(0, 10).map((profile, index) => createLeaderboardEntry(profile, index)),
    wordsSubmitted: [...profiles]
      .sort((a, b) => (b.words_submitted || 0) - (a.words_submitted || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.words_submitted || 0)),
    alternativesProposed: [...profiles]
      .sort((a, b) => (b.alternatives_proposed || 0) - (a.alternatives_proposed || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.alternatives_proposed || 0)),
    votesCast: [...profiles]
      .sort((a, b) => (b.votes_cast || 0) - (a.votes_cast || 0))
      .slice(0, 10)
      .map((profile, index) => createLeaderboardWithCount(profile, index, profile.votes_cast || 0)),
  }

  // Map voting history
  const votingHistoryMap: Record<string, any> = {}
  votingHistory.forEach(vote => {
    votingHistoryMap[vote.id] = {
      id: vote.id,
      userId: effectiveUserId || '',
      alternativeId: vote.alternative_id,
      foreignWord: vote.alternative?.foreign_word?.word || '',
      foreignWordArabic: vote.alternative?.foreign_word?.word_arabic || '',
      alternativeText: vote.alternative?.egyptian_word || '',
      voteType: vote.vote_type,
      votedAt: vote.voted_at,
    }
  })

  // Map the profile user (the one being viewed)
  const profileUser = (() => {
    if (!effectiveUserId) {
      return {
        id: '',
        name: 'Guest',
        avatarUrl: null,
        joinDate: new Date().toISOString(),
        wordsSubmitted: 0,
        alternativesProposed: 0,
        votesCast: 0,
        commentsLeft: 0,
        totalScore: 0,
        rank: 0,
      }
    }

    const userProfile = targetUser || profiles.find(p => p.id === effectiveUserId)

    if (userProfile) {
      // Calculate rank
      const rank = profiles.findIndex(p => p.id === effectiveUserId) + 1

      return {
        id: userProfile.id,
        name: userProfile.name || 'User',
        avatarUrl: userProfile.avatar_url,
        joinDate: userProfile.join_date || userProfile.created_at,
        wordsSubmitted: userProfile.words_submitted || 0,
        alternativesProposed: userProfile.alternatives_proposed || 0,
        votesCast: userProfile.votes_cast || 0,
        commentsLeft: userProfile.comments_left || 0,
        totalScore: userProfile.total_score || 0,
        rank: rank,
      }
    }

    // Fallback
    return {
      id: effectiveUserId,
      name: currentAuthUser?.user_metadata?.name || currentAuthUser?.email?.split('@')[0] || 'User',
      avatarUrl: currentAuthUser?.user_metadata?.avatar_url,
      joinDate: new Date().toISOString(),
      wordsSubmitted: 0,
      alternativesProposed: 0,
      votesCast: 0,
      commentsLeft: 0,
      totalScore: 0,
      rank: 0,
    }
  })()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  // Check if viewing own profile
  const isOwnProfile = currentAuthUser?.id === effectiveUserId

  return (
    <div>
      <CommunityProfile
        users={users}
        userProfiles={userProfilesRecord}
        leaderboards={leaderboards}
        votingHistory={votingHistoryMap}
        currentUser={profileUser}
        isOwnProfile={isOwnProfile}
        allBadges={allBadges}
        userBadges={userBadges}
        onViewProfile={handleViewProfile}
        onViewVotingHistory={handleViewVotingHistory}
      />
    </div>
  )
}
